import { useEffect, useState } from 'react';
import { Input, Button, Alert, Badge, useToast } from '@dreamtree-org/twreact-ui';
import { SectionCard, QueryState } from '@/components';
import { errorText } from '@/lib/format';
import { useQuery, useMutation } from '@/lib/useQuery';
import { emailForm, emailPayload } from '../../lib/emailSettings';

/**
 * Store settings → Email (MERCH-107, D117): the store's own SMTP. Vendor invites and
 * resets and staff invites go out through it; until it is set up nothing is emailed
 * and the admin shares links by hand. Owner only (store:email; StoreSettings hides
 * the tab, the server refuses everyone else). The password is never sent back: a
 * blank field keeps the stored one. TLS follows the port (465 TLS, others STARTTLS).
 */
export default function EmailTab() {
  const { toast } = useToast();
  const q = useQuery('storeEmailSettings', {}, { select: (r) => r && r.data });
  const save = useMutation('saveStoreEmailSettings');
  const test = useMutation('testStoreEmail');
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);
  // Typed every time, never prefilled or saved (user, 2026-11-03): the sign-in
  // email may not be a real inbox.
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    if (q.data) setForm(emailForm(q.data));
  }, [q.data]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const r = await save.run(emailPayload(form, q.data.version));
      q.setData(r.data);
      toast.success('Email settings saved', 'Send a test email to check them.');
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  const sendTest = async () => {
    setTestResult(null);
    try {
      const r = await test.run({ to: testTo.trim() });
      setTestResult(r.data);
    } catch (ex) {
      setTestResult({ sent: false, message: errorText(ex) });
    }
  };

  return (
    <QueryState query={q}>
      {(data) => (form ? (
        <SectionCard title="Email">
          <div className="flex items-center gap-2">
            {data.configured ? <Badge variant="success" size="sm">Set up</Badge> : <Badge variant="warning" size="sm">Not set up</Badge>}
            <p className="text-sm opacity-70">Vendor and staff invitations and password resets are sent from your own email server.</p>
          </div>
          {!data.configured ? (
            <Alert variant="info" title="Not set up">Until you add your SMTP details, nothing is emailed: you get a link to share yourself when you add a vendor or invite staff.</Alert>
          ) : null}
          <form className="space-y-3" onSubmit={submit}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="SMTP server" value={form.host} onChange={set('host')} placeholder="smtp.example.com" required maxLength={253} />
              <Input label="Port" type="number" inputMode="numeric" value={form.port} onChange={set('port')} helperText="587 (STARTTLS) or 465 (TLS)" required />
              <Input label="Username" value={form.username} onChange={set('username')} autoComplete="off" maxLength={255} />
              <Input
                label="Password"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={set('password')}
                helperText={data.password_set ? 'Saved. Leave blank to keep it.' : 'Your SMTP or app password.'}
              />
              <Input label="From email" type="email" value={form.from_email} onChange={set('from_email')} placeholder="orders@yourstore.in" required />
              <Input label="From name" value={form.from_name} onChange={set('from_name')} placeholder="Your store name" maxLength={120} />
            </div>
            {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" loading={save.loading}>Save</Button>
            </div>
          </form>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1 sm:max-w-sm">
              <Input
                label="Send a test email to"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@example.com"
                autoComplete="off"
                helperText={data.configured ? 'Type any inbox you can check.' : 'Save your email settings first.'}
              />
            </div>
            <Button type="button" variant="outline" onClick={sendTest} loading={test.loading} disabled={!data.configured || !testTo.trim()}>Send test email</Button>
          </div>
          {testResult ? (
            testResult.sent
              ? <Alert variant="success" title="Test email sent">Sent to {testResult.to}. Check that inbox (and spam).</Alert>
              : <Alert variant="error" title="Test email not sent">{testResult.message || `The mail server refused it (${testResult.error_code}). Check the server, port, username and password.`}</Alert>
          ) : null}
        </SectionCard>
      ) : null)}
    </QueryState>
  );
}
